import React, { useState, useEffect, useRef } from "react";
import { FaArrowRight } from "react-icons/fa";
import axios from "axios";
import AudioPlayer from "../newplayer/AudioPlayer";
import Swal from "sweetalert2";
import { FormControlLabel, FormGroup, Switch } from "@mui/material";
import { usePreference } from "../../context/UserContext";
import RewardPointsCard from "../login/RewardPointsCard";
import { useNavigate } from "react-router-dom";
import "./ImageSlider.css";
import BEEIMG from "../../assets/img/bee.png";

export default function ImageSliderRedesign({ onClose }) {
  const MAX_CHANCES = 3;

  const [slides, setSlides] = useState([]);
  const [resultMessage, setResultMessage] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [isDisabled, setIsDisabled] = useState(false); // audio error etc
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [validateDisabled, setValidateDisabled] = useState(false); // when correct or exhausted
  const [word, setWord] = useState("");
  const [chancesLeft, setChancesLeft] = useState(MAX_CHANCES);

  const inputRef = useRef(null);
  const { updateIsLoginOpen } = usePreference();
  const navigate = useNavigate();

  const currentSlide = slides[0];
  
  // compute whether quiz button should be enabled (only after all attempts used)
  const quizEnabled = currentSlide?.QuizId && chancesLeft <= 0;

  // Load today's slide and subscription status
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://s3napi-dev.s3nsoftware.com/api/WordOfTheDay?date=${today}`
        );
        setSlides(response.data || []);
      } catch (err) {
        console.error("Error fetching WordOfTheDay:", err);
      }
    };

    const checkSubscription = async () => {
      const user = localStorage.getItem("username");
      if (!user) return;
      try {
        const res = await axios.get(
          `https://s3napi-dev.s3nsoftware.com/api/QuizDatas/GetWordSubscribe?UserName=${user}`
        );
        if (res.data === "subscribed") setIsSubscribed(true);
      } catch (err) {
        console.error("Error checking subscription:", err);
      }
    };

    fetchData();
    checkSubscription();
  }, []);

  // When slide changes: determine attempts (server for logged-in, local for anonymous)
  useEffect(() => {
    if (!currentSlide) {
      setChancesLeft(MAX_CHANCES);
      setResultMessage(null);
      setValidateDisabled(false);
      setInputValue("");
      return;
    }

    const username = localStorage.getItem("username");

    const updateFromServer = async () => {
      try {
        const response = await axios.get(
          `https://s3napi-dev.s3nsoftware.com/api/WordOfTheDay/AttemptedWordSummery?username=${username}`
        );

        // If server returns empty or non-string, treat as no attempts
        if (
          !response.data ||
          typeof response.data !== "string" ||
          response.data.trim() === ""
        ) {
          setChancesLeft(MAX_CHANCES);
          setResultMessage(null);
          setValidateDisabled(false);
          setInputValue("");
          return;
        }

        const attempts = response.data
          .split(",")
          .map((a) => a.trim())
          .filter(Boolean);

        const remaining = Math.max(0, MAX_CHANCES - attempts.length);
        setChancesLeft(remaining);

        if (attempts.length > 0) {
          setResultMessage(attempts.map((a, i) => `${i + 1}.  ${a}`).join(" "));
        } else {
          setResultMessage(null);
        }

        const actualWord = (currentSlide.Word || "").trim().toLowerCase();
        const alreadyCorrect = attempts.some(
          (it) => it.toLowerCase() === actualWord
        );

        if (alreadyCorrect || attempts.length >= MAX_CHANCES) {
          setInputValue(actualWord);
          setValidateDisabled(true);
        } else {
          setValidateDisabled(false);
          setInputValue("");
        }
      } catch (err) {
        console.error("Error fetching attempts from server:", err);
        // fallback to local
        readLocalAttempts();
      }
    };

    const readLocalAttempts = () => {
      try {
        const attemptKey = `attempts_${currentSlide.Id}`;
        const existing = localStorage.getItem(attemptKey);
        const attempts = existing ? JSON.parse(existing) : [];

        const remaining = Math.max(0, MAX_CHANCES - attempts.length);
        setChancesLeft(remaining);

        if (attempts.length > 0) {
          setResultMessage(attempts.map((a, i) => `${i + 1}.  ${a}`).join(" "));
        } else {
          setResultMessage(null);
        }

        const actualWord = (currentSlide.Word || "").trim().toLowerCase();
        const alreadyCorrect = attempts.some(
          (it) => it.toLowerCase() === actualWord
        );

        if (alreadyCorrect || attempts.length >= MAX_CHANCES) {
          setInputValue(actualWord);
          setValidateDisabled(true);
        } else {
          setValidateDisabled(false);
          setInputValue("");
        }
      } catch (err) {
        console.error("Error reading local attempts:", err);
        setChancesLeft(MAX_CHANCES);
        setResultMessage(null);
        setValidateDisabled(false);
        setInputValue("");
      }
    };

    if (username) {
      updateFromServer();
    } else {
      readLocalAttempts();
    }
  }, [currentSlide]);

  useEffect(() => {
    // disable input when no chances left
    if (chancesLeft <= 0) {
      setValidateDisabled(true);
    }
  }, [chancesLeft]);

  const handleAudioComplete = () => inputRef.current?.focus();
  const handleAudioError = (err) => {
    console.error("Audio error:", err);
    setIsDisabled(true);
  };

  const speakMessage = (msg) => {
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(msg);
      u.lang = "en-US";
      speechSynthesis.speak(u);
    }
  };

  // VALIDATION / ATTEMPT HANDLING
  const validateAnswer = async (userInput) => {
    if (!currentSlide) return;

    if (validateDisabled || chancesLeft <= 0) {
      // Already disabled — don't allow extra submissions.
      return;
    }

    if (!userInput || userInput.trim() === "") {
      setResultMessage("Please enter the word");
      return;
    }

    const actualWord = (currentSlide.Word || "").trim().toLowerCase();
    const input = userInput.trim().toLowerCase();
    const isCorrect = actualWord === input;

    const attemptKey = `attempts_${currentSlide.Id}`;
    let attempts = [];

    const username = localStorage.getItem("username");

    // --- LOGGED-IN: POST to server and read back attempts (server is single source) ---
    if (username) {
      try {
        const payload = {
          Id: 0,
          Word: currentSlide.Word || "",
          QuizType: currentSlide.Quiztype || null,
          AnswerType: isCorrect ? 1 : 2,
          Username: username,
          Answer: currentSlide.Word,
          InputAnswer: userInput,
          WordId: currentSlide.Id || null,
          InsertedDate: currentSlide.Date,
        };

        const res = await fetch(
          "https://s3napi-dev.s3nsoftware.com/api/WordOfTheDay/AttemptWord",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        if (!res.ok) {
          // server error -> fallback to local handling but do not crash UI
          console.error("Server responded with error:", res.statusText);
          // fallback to local attempt handling below
          throw new Error("Server error");
        }

        // server returns comma-separated attempts as text
        const text = (await res.text()).replace(/\"/g, "");
        attempts = text
          .split(",")
          .map((a) => a.trim().toLowerCase())
          .filter(Boolean);

        // update UI state from server result
        setResultMessage(
          attempts.length
            ? attempts.map((a, i) => `${i + 1}.  ${a}`).join(" ")
            : null
        );
        setChancesLeft(Math.max(0, MAX_CHANCES - attempts.length));

        const isAlreadyCorrect = attempts.some(
          (it) => it.toLowerCase() === actualWord
        );

        if (isCorrect || isAlreadyCorrect || attempts.length >= MAX_CHANCES) {
          if (isCorrect && !isAlreadyCorrect) {
            // If server accepted this as correct, ensure input shows the word
            setInputValue(actualWord);
          } else {
            setInputValue(actualWord);
          }
          setValidateDisabled(true);
          await speakMessage(
            isCorrect ? "Correct" : `The correct answer is ${actualWord}`
          );
        } else {
          // incorrect but still have chances
          setValidateDisabled(false);
        }

        return;
      } catch (err) {
        console.error("Error posting attempt to server:", err);
        // fallback to local below
      }
    }

    // --- ANONYMOUS / FALLBACK: store attempts in localStorage ---
    try {
      const existing = localStorage.getItem(attemptKey);
      attempts = existing ? JSON.parse(existing) : [];

      const actualLower = actualWord.toLowerCase();
      const alreadyCorrect = attempts.some(
        (it) => it.toLowerCase() === actualLower
      );

      // If already correct or attempts exhausted, just enforce disabled behavior
      if (alreadyCorrect) {
        setInputValue(actualWord);
        setValidateDisabled(true);
        setChancesLeft(Math.max(0, MAX_CHANCES - attempts.length));
        setResultMessage(
          attempts.length
            ? attempts.map((a, i) => `${i + 1}.  ${a}`).join(" ")
            : null
        );
        return;
      }

      if (attempts.length >= MAX_CHANCES) {
        // No more attempts allowed
        setChancesLeft(0);
        setValidateDisabled(true);
        setInputValue(actualWord);
        setResultMessage(
          attempts.length
            ? attempts.map((a, i) => `${i + 1}.  ${a}`).join(" ")
            : null
        );
        await Swal.fire({
          title: "No attempts left",
          text: "You have used all attempts. Login to save progress and continue.",
          icon: "info",
          confirmButtonText: "Login",
        }).then((res) => {
          if (res.isConfirmed) updateIsLoginOpen(true);
        });
        return;
      }

      // Accept this attempt (correct or incorrect) and store it
      attempts.push(input);
      localStorage.setItem(attemptKey, JSON.stringify(attempts));

      setResultMessage(
        attempts.length
          ? attempts.map((a, i) => `${i + 1}.  ${a}`).join(" ")
          : null
      );
      setChancesLeft(Math.max(0, MAX_CHANCES - attempts.length));

      const isAlreadyCorrectAfterPush = attempts.some(
        (it) => it.toLowerCase() === actualLower
      );

      if (
        isCorrect ||
        isAlreadyCorrectAfterPush ||
        attempts.length >= MAX_CHANCES
      ) {
        // If answered correctly or attempts exhausted, reveal and disable
        setInputValue(actualWord);
        setValidateDisabled(true);
        await speakMessage(
          isCorrect ? "Correct" : `The correct answer is ${actualWord}`
        );
      } else {
        // still have chances and wrong answer
        setValidateDisabled(false);
      }
    } catch (err) {
      console.error("Error storing local attempt:", err);
    }
  };

  const getSpecialCharacters = (inputString) => {
    const specialCharacterRegex = /[^a-zA-Z0-9\s]/g;
    const specialCharactersArray = inputString.match(specialCharacterRegex);
    const specialCharsSet = new Set(specialCharactersArray || []);
    if (inputString.includes(" ")) specialCharsSet.add(" ");
    return Array.from(specialCharsSet).join("");
  };

  const checkInputValidations = (spelling) => {
    if (!currentSlide?.Utfascii) return spelling.replace(/[^A-Za-z\s]/g, "");
    const actual_word = currentSlide.Utfascii.trim().toLowerCase();
    const result = getSpecialCharacters(actual_word);
    const fValue = spelling.slice(-1);
    if (!result.includes(fValue)) {
      return spelling.slice(0, -1) + fValue.replace(/[^A-Za-z]/g, "");
    }
    return spelling;
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    const validatedValue = checkInputValidations(newValue);
    setInputValue(validatedValue);
  };

  const handleSubscribe = async () => {
    const user = localStorage.getItem("username");
    if (!user) {
      Swal.fire({
        title: "Login Required",
        text: "Please Login to subscribe",
        icon: "warning",
        confirmButtonText: "Login",
      }).then((result) => {
        if (result.isConfirmed) updateIsLoginOpen(true);
      });
      return;
    }

    if (isSubscribed) {
      Swal.fire("Already Subscribed", "You have already subscribed.", "info");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to subscribe to our newsletter?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, subscribe!",
      cancelButtonText: "No, cancel",
    });

    if (result.isConfirmed) {
      try {
        await axios.get(
          `https://s3napi-dev.s3nsoftware.com/api/QuizDatas/MakeWordSubscribe?UserName=${user}`
        );
        setIsSubscribed(true);
        Swal.fire(
          "Subscribed!",
          "You have successfully subscribed.",
          "success"
        );
      } catch (err) {
        console.error("Subscription failed:", err);
        Swal.fire(
          "Error",
          "Failed to subscribe. Please try again later.",
          "error"
        );
      }
    } else {
      Swal.fire("Cancelled", "You cancelled the subscription.", "error");
    }
  };

  const handleTakeQuiz = () => {
    const username = localStorage.getItem("username");
    if (!username) {
      Swal.fire({
        title: "Login Required",
        text: "Please Login to subscribe",
        icon: "warning",
        confirmButtonText: "Login",
      }).then((result) => {
        if (result.isConfirmed) updateIsLoginOpen(true);
      });
      return;
    }

    axios
      .get(
        "https://s3napi-dev.s3nsoftware.com/api/QuizDatas/GetCatIdByQuizId",
        {
          params: { QuizId: currentSlide?.QuizId, username: username },
          headers: { Accept: "application/json" },
        }
      )
      .then((response) => {
        navigate(`/playquiz/${response.data}`);
      })
      .catch((error) => console.error("Error fetching data:", error));
  };

  const maskWordInText = (text, word) => {
    if (!text || !word) return text;

    // Escape special regex characters in the word
    const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const regex = new RegExp(`\\b${escapedWord}\\b`, "gi");

  return text.replace(regex, "____");
  };

  return (
    <div className="wrapper" onClick={(e) => e.stopPropagation()}>
      <div className="topSection">
        <div className="topLeft">
          <RewardPointsCard />
        </div>

        <div className="centerTop">
         <p class="heading-highlight">Word Of The Day</p>

          {/* <div className="beeArea">
            <div className="beeCircle">
              <img src={BEEIMG} alt="bee" />
             
            </div>
          </div> */}
        </div>

        <div className="topRight">
          {!isSubscribed && (
            <button className="round-button topBtn" onClick={handleSubscribe}>
              Subscribe
            </button>
          )}
          {currentSlide?.QuizId && (
            <button
              className={`round-button topBtn ${
                !quizEnabled ? "disabled-quiz-btn" : ""
              }`}
              onClick={handleTakeQuiz}
              disabled={!quizEnabled}
              title={
                !quizEnabled
                  ? "Complete all attempts to enable quiz"
                  : "Take a Quiz"
              }
            >
              Take a Quiz
            </button>
          )}
        </div>
      </div>

      <div className="inputArea inputAreaMain">
        {currentSlide?.Audio && (
          <AudioPlayer
            onAudioComplete={handleAudioComplete}
            audioUrl={
              currentSlide.Audio.startsWith("sound://")
                ? currentSlide.Audio.replace(
                    "sound://",
                    "https://s3nsoftware.com/"
                  )
                : currentSlide.Audio
            }
            currentIndex={0}
            color={"#fff"}
            init={false}
            quiz={true}
            replay={false}
            isDisabled={isDisabled}
            bee={"0px"}
          />
        )}

        <div className="spellingInput">
          <input
            placeholder="Enter Spelling"
            className="searchInput"
            type="text"
            spellCheck="false"
            onContextMenu={(e) => e.preventDefault()}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onDrag={(e) => e.preventDefault()}
            onDrop={(e) => e.preventDefault()}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            value={inputValue}
            onChange={handleChange}
            ref={inputRef}
            disabled={validateDisabled || chancesLeft <= 0}
          />
          <button
            className="playButton"
           // style={{borderRadius: '50%'}}
            onClick={() => validateAnswer(inputValue)}
            disabled={validateDisabled || chancesLeft <= 0}
          >
            <FaArrowRight />
          </button>
        </div>
        <div className="leftControls">
          <FormGroup>
            <FormControlLabel
            sx={{
              "& .MuiFormControlLabel-label": {
                color: "#000", // black
              },
            }}
              control={
                <Switch
                  checked={showHints}
                  onChange={(e) => setShowHints(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#000" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#000",
                    },
                  }}
                />
              }
              label="Show Hints"
            />
          </FormGroup>
        </div>
      </div>

      <div className="answerListArea">
        {!(
          validateDisabled &&
          inputValue &&
          inputValue.toLowerCase() ===
            (currentSlide?.Word || "").toLowerCase() &&
          chancesLeft > 0
        ) && (
          <div
            className="chancesContainer"
            style={{ marginBottom: 8, textAlign: "left" }}
          >
            <div className="chancesInfo" data-remaining={chancesLeft} style={{ color: "#000" }}>
              {chancesLeft > 0
                ? `${chancesLeft} ${
                    chancesLeft === 1 ? "chance" : "chances"
                  } remaining`
                : "No chances left!, Come tomorrow for new word"}
            </div>
          </div>
        )}
        {resultMessage && (
          <div className="resultMessage">
            {resultMessage.split(/(?=\d+\.\s)/).map((item, idx) => {
              const cleanItem = item.replace(/^\d+\.\s*/, "").trim();
              const isCorrect =
                cleanItem === (currentSlide?.Word || "").toLowerCase();
              return (
                <span
                  key={idx}
                  style={{
                    color: isCorrect ? "green" : "#fdc8bf",
                    marginRight: 8,
                  }}
                >
                  {item}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {currentSlide ? (
        <div className="cardsArea">
          <div className="leftColumn">
            <div className="ImageSlidercard small">
              <div className="cardHeader">PART OF SPEECH</div>
              <div className="cardBody">
                {showHints ? currentSlide.Pos || "noun" : ""}
              </div>
            </div>

            <div className="ImageSlidercard small">
              <div className="cardHeader">ETYMOLOGY</div>
              <div className="cardBody">
                {showHints ? currentSlide.Etym || "Latin" : ""}
              </div>
            </div>
          </div>

          <div className="rightColumn">
            <div className="ImageSlidercard">
              <div className="cardHeader">DEFINITION</div>
              <div className="cardBody">
                {showHints ? currentSlide.Defn : ""}
              </div>
            </div>

            <div className="ImageSlidercard">
              <div className="cardHeader">VOCABULARY</div>
              {/* <div className="cardBody">{showHints ? currentSlide.Vocabulary : ""}</div> */}
              <div className="cardBody">
                {showHints
                  ? maskWordInText(currentSlide.Vocabulary, currentSlide.Word)
                  : ""}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="noEntries">No entries found for today.</div>
      )}
    </div>
  );
}
